import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHandshake, FaEdit, FaTrash, FaCarSide, FaPercent, FaChevronDown, FaSearch, FaPlus } from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import moment from 'moment-timezone';
import Swal from 'sweetalert2';
import api from '../../services/api';
import CommissionReport from './CommissionReport';
import CreatePartnerModal from './CreatePartnerModal';
import CreatePartnerRentalModal from './CreatePartnerRentalModal';
import TransferModal from '../TransferModal/TransferModal';
import CreateRentalModal from '../RentalModal/CreateRentalModal';

const PartnerManagement = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerRentals, setPartnerRentals] = useState([]);
  const [expandedRental, setExpandedRental] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateRentalModalOpen, setIsCreateRentalModalOpen] = useState(false);
  const [isCreateTransferModalOpen, setIsCreateTransferModalOpen] = useState(false);
  const [isCreatingPartner, setIsCreatingPartner] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchPartners = async () => {
    try {
      const response = await api.partners.getAll();
      setPartners(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching partners:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch partners',
      });
    }
  };

  const fetchPartnerRentals = async (partnerId) => {
    try {
      const response = await api.partners.getRentals(partnerId);
      setPartnerRentals(response.data);
    } catch (error) {
      console.error('Error fetching partner rentals:', error);
      setPartnerRentals([]);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

    useEffect(() => {
    if (selectedPartner) {
      fetchPartnerRentals(selectedPartner._id);
      fetchPartnerTransfers(selectedPartner._id);
    }
  }, [selectedPartner]);

  const handleCreatePartner = async (data) => {
    try {
      setIsCreatingPartner(true);
      const response = await api.partners.create(data);
      
      setIsCreateModalOpen(false);
      await Swal.fire({
        icon: 'success',
        title: 'Partner Created Successfully',
        text: `${data.firstName} ${data.lastName} has been added as a partner.`,
        customClass: {
          popup: 'rounded-xl animate__animated animate__fadeInUp',
          title: 'text-2xl font-bold text-gray-900',
          content: 'text-gray-600',
          confirmButton: 'bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg transition-colors'
        },
        buttonsStyling: false
      });
      fetchPartners();
    } catch (error) {
      console.error('Error creating partner:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error Creating Partner',
        text: 'Failed to create partner. Please try again.',
        customClass: {
          popup: 'rounded-xl animate__animated animate__fadeInUp',
          title: 'text-2xl font-bold text-gray-900',
          content: 'text-gray-600',
          confirmButton: 'bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg transition-colors'
        },
        buttonsStyling: false
      });
    } finally {
      setIsCreatingPartner(false);
    }
  };

  const handleUpdatePartner = async (partner) => {
    try {
      const result = await Swal.fire({
        title: 'Update Partner',
        html: `
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Partner Name</label>
              <input id="firstName" value="${partner.firstName}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Manager Name</label>
              <input id="lastName" value="${partner.lastName}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Email</label>
              <input id="email" type="email" value="${partner.email}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Phone Number</label>
              <input id="phoneNumber" value="${partner.phoneNumber}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Commission Rate (%)</label>
              <input id="commissionRate" type="number" min="0" max="100" value="${partner.commissionRate}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Update',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#1e3a8a',
        preConfirm: () => {
          const updates = {
            firstName: document.getElementById('firstName').value,
            lastName: document.getElementById('lastName').value,
            email: document.getElementById('email').value,
            phoneNumber: document.getElementById('phoneNumber').value,
            commissionRate: Number(document.getElementById('commissionRate').value)
          };

          if (!updates.firstName || !updates.lastName || !updates.email || !updates.phoneNumber || !updates.commissionRate) {
            Swal.showValidationMessage('Please fill in all fields');
            return false;
          }

          return updates;
        }
      });

      if (result.isConfirmed) {
        await api.partners.update(partner._id, result.value);
        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Partner updated successfully!',
        });
        fetchPartners();
      }
    } catch (error) {
      console.error('Error updating partner:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update partner',
      });
    }
  };

  const handleDeletePartner = async (partnerId) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "This action cannot be undone!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#1e3a8a',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete partner'
      });

      if (result.isConfirmed) {
        await api.partners.delete(partnerId);
        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Partner deleted successfully!',
        });
        fetchPartners();
        if (selectedPartner?._id === partnerId) {
          setSelectedPartner(null);
          setPartnerRentals([]);
        }
      }
    } catch (error) {
      console.error('Error deleting partner:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete partner',
      });
    }
  };

  const handleRentalCreated = (rental) => {
    // Add the new rental to the list and refresh data
    fetchPartnerRentals(selectedPartner._id);
    
    Swal.fire({
      icon: 'success',
      title: 'Success',
      text: 'Rental created successfully!',
      confirmButtonColor: '#1e3a8a',
    });
  };

  const handleTransferCreated = (transfer) => {
    // Add the new transfer to the list and refresh data
    fetchPartnerTransfers(selectedPartner._id);
  };

  const calculateRentalDuration = (pickupDateTime, returnDateTime) => {
    return Math.ceil(
      Math.abs(new Date(returnDateTime) - new Date(pickupDateTime)) / (1000 * 60 * 60 * 24)
    );
  };

      const formatPrice = (price) => {
    const numericPrice = parseFloat(price);
    return `€${(isNaN(numericPrice) ? 0 : numericPrice).toFixed(2)}`;
  };

  // Helper function to calculate correct rental totals
  const calculateRentalTotals = (rental) => {
    const rentalDays = calculateRentalDuration(rental.pickupDateTime, rental.returnDateTime);
    
    // Check if we should use custom pricing or existing price breakdown
    const useCustomCalculation = rental.customPricePerDay && (
      !rental.priceBreakdown || 
      rental.priceBreakdown.length === 0 || 
      rental.priceBreakdown.some(period => period.pricePerDay === 0)
    );
    
    let totalRentalPrice = 0;
    let additionalOptionsTotal = 0;
    
    if (useCustomCalculation) {
      // Use custom price per day calculation
      const pricePerDay = rental.customPricePerDay;
      totalRentalPrice = rentalDays * pricePerDay;
      
      // Calculate additional options based on actual options array
      additionalOptionsTotal = rental.additionalOptions ? 
        rental.additionalOptions.reduce((total, option) => {
          const optionPrice = parseFloat(option.price) || 0;
          const optionTotal = isNaN(optionPrice) ? 0 : optionPrice * rentalDays;
          return total + optionTotal;
        }, 0) : 0;
    } else {
      // Use existing price breakdown
      totalRentalPrice = rental.priceBreakdown ? 
        rental.priceBreakdown.reduce((sum, period) => sum + period.totalForPeriod, 0) : 
        (rental.basePrice * rentalDays);
      
            // Calculate additional options based on actual options array
      additionalOptionsTotal = rental.additionalOptions ? 
        rental.additionalOptions.reduce((total, option) => {
          const optionPrice = parseFloat(option.price) || 0;
          const optionTotal = isNaN(optionPrice) ? 0 : optionPrice * rentalDays;
          return total + optionTotal;
        }, 0) : 0;
        console.log('Additional Options Total:', additionalOptionsTotal);
        console.log('Total Rental Price:', totalRentalPrice);
    }
    
    // Ensure no NaN values propagate
    totalRentalPrice = isNaN(totalRentalPrice) ? 0 : totalRentalPrice;
    additionalOptionsTotal = isNaN(additionalOptionsTotal) ? 0 : additionalOptionsTotal;
    
    const subtotal = totalRentalPrice + additionalOptionsTotal;
    const totalAfterDiscounts = subtotal - (rental.discountAmount || 0);
    
    // Commission is calculated on the total BEFORE VAT (divide by 1.24 to remove VAT)
    const totalBeforeVat = totalAfterDiscounts / 1.24;
    const correctedCommission = totalBeforeVat * (selectedPartner.commissionRate / 100);
    
    return {
      totalRentalPrice,
      additionalOptionsTotal,
      subtotal,
      totalAfterDiscounts,
      totalBeforeVat,
      correctedCommission,
      useCustomCalculation,
      rentalDays
    };
  };

  // Filter partners based on search query
  const filteredPartners = partners.filter(partner =>
    partner.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    partner.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    partner.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    partner.phoneNumber.includes(searchQuery)
  );

  if (loading) {
    return <LoadingSpinner />;
  }
  const fetchPartnerTransfers = async (partnerId) => {
    try {
      const response = await api.contact.getPartnerTransfers(partnerId);
      setTransfers(response.data);
    } catch (error) {
      console.error('Error fetching transfers:', error);
    }
  };



  return (
    <div className="space-y-6">
      {/* Partners Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Partner Management</h2>
          <p className="text-gray-500">Manage your professional partners and their commissions</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="px-4 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300"
        >
          Create Partner
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative w-64">
        <input
          type="text"
          placeholder="Search partners..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Partners List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Partners</h3>
            </div>
            <div className="divide-y divide-gray-200">
              <AnimatePresence>
                {filteredPartners.map((partner) => (
                  <motion.div
                    key={partner._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={`p-4 cursor-pointer transition-colors duration-150 ${
                      selectedPartner?._id === partner._id
                        ? 'bg-blue-50'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedPartner(partner)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {partner.firstName}
                        </h4>
                        <p className="text-sm text-gray-500">Manager: {partner.lastName}</p>
                        <p className="text-sm text-gray-500">Email: {partner.email}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdatePartner(partner);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-500"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePartner(partner._id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Partner Details and Rentals */}
        <div className="lg:col-span-2">
          {selectedPartner ? (
            <div className="space-y-6">
              {/* Partner Details Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-blue-100 rounded-full">
                      <FaHandshake className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {selectedPartner.firstName}
                      </h3>
                      <p className="text-gray-500">Manager: {selectedPartner.lastName}</p>
                    </div>
                  </div>
                  <div className='flex items-center space-x-2'>
                  <motion.button
                    onClick={() => setIsCreateRentalModalOpen(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-300 flex items-center space-x-2"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <FaPlus className="w-4 h-4" />
                    <span>Create Rental</span>
                  </motion.button>
                              <motion.button
              onClick={() => setIsCreateTransferModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300 flex items-center space-x-2"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FaPlus className="w-4 h-4" />
              <span>Create Transfer</span>
            </motion.button>
            </div>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-500">Phone Number</p>
                    <p className="text-lg font-medium text-gray-900">
                      {selectedPartner.phoneNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Commission Rate</p>
                    <p className="text-lg font-medium text-gray-900">
                      {selectedPartner.commissionRate}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Partner Rentals */}
              <div className="space-y-6">
                <CommissionReport 
                  rentals={partnerRentals} 
                  transfers={transfers}
                  partner={selectedPartner} 
                  onRentalsUpdate={() => fetchPartnerRentals(selectedPartner._id)} 
                />
                
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">All Partner Rentals</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {partnerRentals.map((rental) => {
                      const {
                        totalRentalPrice,
                        additionalOptionsTotal,
                        subtotal,
                        totalAfterDiscounts,
                        totalBeforeVat,
                        correctedCommission,
                        useCustomCalculation,
                        rentalDays
                      } = calculateRentalTotals(rental);

                      return (
                        <div key={rental._id} className="cursor-pointer">
                          {/* Rental Summary - Always visible */}
                          <div 
                            className="p-4 hover:bg-gray-50 transition-colors duration-150"
                            onClick={() => setExpandedRental(expandedRental === rental._id ? null : rental._id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="p-2 bg-gray-100 rounded">
                                  <FaCarSide className="w-5 h-5 text-gray-600" />
                                </div>
                                <div>
                                  <h4 className="text-base font-medium text-gray-900">
                                    {rental.carName}
                                  </h4>
                                  <p className="text-sm text-gray-500">
                                    {moment(rental.pickupDateTime).format('MMM D')} - {moment(rental.returnDateTime).format('MMM D, YYYY')}
                                  </p>
                                </div>
                              </div>                            <div className="flex items-center space-x-4">
                              <div className="text-right">                                {(() => {
                                  // Calculate correct totals for summary display
                                  const { totalAfterDiscounts, correctedCommission } = calculateRentalTotals(rental);
                                  
                                  return (
                                    <>
                                      <p className="text-sm font-medium text-gray-900">
                                        Total: {formatPrice(totalAfterDiscounts)}
                                      </p>
                                      <p className="text-sm text-green-600">
                                        Commission: {formatPrice(correctedCommission)}
                                      </p>
                                    </>
                                  );
                                })()}
                              </div>
                              <FaChevronDown 
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                                  expandedRental === rental._id ? 'transform rotate-180' : ''
                                }`}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Expandable Details */}
                        <AnimatePresence>
                          {expandedRental === rental._id && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="px-4 pb-4">
                                {/* Customer Information */}
                                <div className="mb-4 bg-gray-50 rounded-lg p-4">
                                  <h5 className="text-sm font-medium text-gray-700 mb-2">Customer</h5>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-sm text-gray-600">{rental.customer.firstName} {rental.customer.lastName}</p>
                                      <p className="text-sm text-gray-500">{rental.customer.email}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm text-gray-600">{rental.customer.phoneNumber}</p>
                                      <p className="text-sm text-gray-500">Age: {rental.customer.age}</p>
                                    </div>
                                  </div>
                                </div>                                {/* Price Breakdown */}
                                <div className="space-y-2">
                                  <h5 className="text-sm font-medium text-gray-700">Price Breakdown</h5>
                                  
                                  {/* Calculate actual rental duration and show correct pricing */}
                                  {(() => {
                                    const {
                                      totalRentalPrice,
                                      rentalDays,
                                      useCustomCalculation
                                    } = calculateRentalTotals(rental);
                                    
                                    const pricePerDay = rental.customPricePerDay || rental.basePrice;
                                    
                                    return (
                                      <div className="bg-white rounded border border-gray-100 p-3">
                                        <div className="flex justify-between items-start mb-2">
                                          <div>
                                            <p className="text-sm font-medium text-gray-900">
                                              {moment(rental.pickupDateTime).format('MMM D')} - {moment(rental.returnDateTime).format('MMM D, YYYY')}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                              {rentalDays} days at {formatPrice(pricePerDay)}/day
                                              {useCustomCalculation && (
                                                <span className="text-blue-600 ml-1">(Custom Price)</span>
                                              )}
                                            </p>
                                          </div>
                                          <p className="text-sm font-medium text-gray-900">
                                            {formatPrice(totalRentalPrice)}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                    {/* Additional Options Breakdown */}
                                  {rental.additionalOptions && rental.additionalOptions.length > 0 && rental.additionalOptions.some(option => parseFloat(option.price) > 0) && (
                                    <div className="space-y-1">
                                      <h6 className="text-sm font-medium text-gray-700">Additional Options</h6>
                                      {rental.additionalOptions.map((option, index) => {
                                        const { rentalDays } = calculateRentalTotals(rental);
                                        const optionPrice = parseFloat(option.price) || 0;
                                        const optionTotal = isNaN(optionPrice) ? 0 : optionPrice * rentalDays;
                                        
                                        return (
                                          <div key={index} className="bg-blue-50 rounded border border-blue-100 p-3">
                                            <div className="flex justify-between items-start">
                                              <div>
                                                <p className="text-sm font-medium text-gray-900">{option.name}</p>
                                                <p className="text-sm text-gray-500">
                                                  {rentalDays} days at {formatPrice(optionPrice)}/day
                                                </p>
                                              </div>
                                              <p className="text-sm font-medium text-gray-900">
                                                {formatPrice(optionTotal)}
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  
                                    {/* Additional charges and final totals */}
                                  <div className="border-t border-gray-200 pt-2 mt-2">
                                    {/* Calculate total additional options price from the actual options */}
                                    {rental.additionalOptions && rental.additionalOptions.length > 0 && rental.additionalOptions.some(option => parseFloat(option.price) > 0) && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Total Additional Options</span>
                                        <span>
                                          {formatPrice(calculateRentalTotals(rental).additionalOptionsTotal)}
                                        </span>
                                      </div>
                                    )}
                                    {(() => {
                                      // Use the helper function for consistent calculations
                                      const {
                                        totalRentalPrice,
                                        additionalOptionsTotal,
                                        subtotal,
                                        totalAfterDiscounts,
                                        totalBeforeVat,
                                        correctedCommission
                                      } = calculateRentalTotals(rental);
                                      
                                      const vatAmount = totalAfterDiscounts - totalBeforeVat;
                                      
                                      return (
                                        <>
                                          <div className="flex justify-between text-sm pt-2 border-t border-gray-200 mt-2">
                                            <span>Subtotal (before discounts):</span>
                                            <span>{formatPrice(subtotal)}</span>
                                          </div>
                                          {rental.discountAmount > 0 && (
                                            <div className="flex justify-between text-sm">
                                              <span className="text-red-600">Total Discount</span>
                                              <span className="text-red-600">-{formatPrice(rental.discountAmount)}</span>
                                            </div>
                                          )}
                                          <div className="flex justify-between text-sm pt-2 border-t border-gray-200 mt-2">
                                            <span>Before VAT (Tax):</span>
                                            <span>{formatPrice(totalBeforeVat)}</span>
                                          </div>
                                          <div className="flex justify-between text-sm">
                                            <span>VAT (24%):</span>
                                            <span>{formatPrice(vatAmount)}</span>
                                          </div>
                                        </>
                                      );
                                    })()}                                    <div className="flex justify-between text-sm font-medium mt-2">
                                      <span>Total Price (inc. VAT):</span>
                                      <span>
                                        {formatPrice(calculateRentalTotals(rental).totalAfterDiscounts)}
                                      </span>
                                    </div>
                                    
                                    {/* Advance Payment - Only show if greater than 0 */}
                                    {rental.advancePayment > 0 && (
                                      <div className="flex justify-between text-sm text-amber-600 font-medium mt-2">
                                        <span>Advance Payment:</span>
                                        <span>-{formatPrice(rental.advancePayment)}</span>
                                      </div>
                                    )}
                                    
                                    {/* Balance Due - Only show if advance payment exists */}
                                    {rental.advancePayment > 0 && (
                                      <div className="flex justify-between text-sm font-medium mt-2 p-2 bg-blue-50 rounded-md text-blue-800">
                                        <span>Balance Due:</span>
                                        <span>{formatPrice(rental.totalPrice - rental.advancePayment)}</span>
                                      </div>
                                    )}                                      <div className="flex justify-between text-sm text-green-600 font-medium mt-2 pt-2 border-t border-gray-200">
                                      <span>Commission ({selectedPartner.commissionRate}%)</span>
                                      <span>
                                        {formatPrice(calculateRentalTotals(rental).correctedCommission)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                    {partnerRentals.length === 0 && (
                      <div className="p-4 text-center text-gray-500">
                        No rentals found for this partner
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <FaHandshake className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select a Partner
              </h3>
              <p className="text-gray-500">
                Choose a partner from the list to view their details and rentals
              </p>
            </div>
          )}
        </div>
      </div>

      <CreatePartnerModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreatePartner}
        isCreating={isCreatingPartner}
      />

      <CreateRentalModal
        isOpen={isCreateRentalModalOpen}
        isPartnerMode={true}
        onClose={() => setIsCreateRentalModalOpen(false)}
        partner={selectedPartner}
        onSuccess={handleRentalCreated}
      />

      {selectedPartner && (
        <TransferModal
          isOpen={isCreateTransferModalOpen}
          onClose={() => setIsCreateTransferModalOpen(false)}
          partnerId={selectedPartner._id}
          partnerCommissionRate={selectedPartner?.commissionRate}
          onSuccess={handleTransferCreated}
        />
      )
      }
              
    </div>
  );
};

export default PartnerManagement;